<template>
    <PlanbordGridRow :data-y="index">
        <PlanbordGridLeftDataColumns>
            <CompanyEmployeeNameCell v-if="companyEmployee" :companyEmployee="companyEmployee" :company="company" />
            <CompanyNameCell
                v-else
                :company="company"
                :showOpenShiftMarketplaceFunctions="showOpenShiftMarketplaceFunctions"
                @open:addOpenShiftMarketplaceEmployee="$emit('open:addOpenShiftMarketplaceEmployee', $event)"
            />

            <PlanbordFunctionCell />
            <PlanbordCommentCell />
            <PlanbordStampStartDateCell />
        </PlanbordGridLeftDataColumns>

        <PlanbordCell
            v-for="(day, dayIndex) in roster.days"
            :key="'CompanyDeploymentCell|' + planbordCellKey(day, dayIndex)"
            :day="day"
            :dayIndex="dayIndex"
            :resourceIndex="index"
            :leavePeriods="[]"
            :hasLeaveForm="false"
            :company="company"
            :companyEmployee="companyEmployee"
            :roster="roster"
            :rosterNames="rosterNames"
            :ref="(el: PlanbordCell | null) => cells[Number(dayIndex)] = el"
            :organisationDeploymentStates="organisationDeploymentStates"
            :shifts="shifts"
            :permissions="permissions"
            @create:deployment="$emit('create:deployment', $event)"
            @update:deployment="$emit('update:deployment', $event)"
            @delete:deployment="$emit('delete:deployment', $event)"
            @create:stampShift="$emit('create:stampShift', $event)"
        />

        <PlanbordGridRightDataColumns>
            <BalanceCompanyOrOtherRoster
                :timeTypes="roster.timeTypes"
                :leaveOverviews="leaveOverviews"
                :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                :deploymentCounts="deploymentCounts"
            />
        </PlanbordGridRightDataColumns>
    </PlanbordGridRow>
</template>

<script lang="ts">
    import PlanbordGridRow from '@/components/Planbord/PlanbordGridRow.vue';
    import CompanyNameCell from '@/components/Planbord/CompanyNameCell.vue';
    import CompanyEmployeeNameCell from '@/components/Planbord/CompanyEmployeeNameCell.vue';
    import BalanceCompanyOrOtherRoster from '@/components/Planbord/Balance/BalanceCompanyOrOtherRoster.vue';
    import PlanbordCell from '@/components/Planbord/PlanbordCell.vue';
    import {RosterData, RosterDay, PermissionsForRoster} from '@/types/types';
    import {LeaveOverview, OrganisationDeploymentState, Shift} from '@cquential/api';
    import {BalanceFilterType} from '@/components/FilterBar/types';
    import {DeploymentCountsOfResourceOrCompany} from '@/components/types';
    import {CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments} from '@/types/api-object-extensions';
    import PlanbordGridLeftDataColumns from '@/components/Planbord/PlanbordGridLeftDataColumns.vue';
    import PlanbordFunctionCell from '@/components/Planbord/PlanbordFunctionCell.vue';
    import PlanbordCommentCell from '@/components/Planbord/PlanbordCommentCell.vue';
    import PlanbordGridRightDataColumns from '@/components/Planbord/PlanbordGridRightDataColumns.vue';
    import PlanbordStampStartDateCell from '@/components/Planbord/PlanbordStampStartDateCell.vue';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            PlanbordGridRow,
            CompanyNameCell,
            CompanyEmployeeNameCell,
            BalanceCompanyOrOtherRoster,
            PlanbordCell,
            PlanbordGridLeftDataColumns,
            PlanbordFunctionCell,
            PlanbordCommentCell,
            PlanbordGridRightDataColumns,
            PlanbordStampStartDateCell,
        },
        emits: [
            'open:addOpenShiftMarketplaceEmployee',
            'create:deployment',
            'update:deployment',
            'delete:deployment',
            'create:stampShift',
        ],
    })
    export default class PlanbordGridRowCompany extends Vue {
        @Prop({required: true}) public company!: CompanyWithDeploymentsAndEmployees;
        @Prop() public companyEmployee?: CompanyEmployeeWithDeployments;
        @Prop({type: Number, required: true}) public index!: number;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({required: true}) public enabledBalanceAndCountDisplays!: Record<BalanceFilterType, Record<number, boolean>>;
        @Prop({required: true}) public leaveOverviews!: LeaveOverview[];
        @Prop({required: true}) public deploymentCounts!: DeploymentCountsOfResourceOrCompany[];
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public permissions!: Record<number, PermissionsForRoster>;

        private cells: Record<number, PlanbordCell | null> = {};

        public getCell(position: number): PlanbordCell | undefined {
            return this.cells[position] ?? undefined;
        }

        public planbordCellKey(day: RosterDay, dayIndex: number): string {
            const preFix: string = this.companyEmployee ? this.companyEmployee.id + '|' : '';

            return preFix + 'index-' + dayIndex + '|day-' + day.date.getTime();
        }

        public get showOpenShiftMarketplaceFunctions(): boolean {
            return this.roster.usesMarketplaceFunctions && this.company.openShiftMarketplaceSyncEnabled;
        };
    }
</script>
